import { useTranslation } from 'react-i18next';
import { useRemoveWhishlistedHolidayMutation } from '@api/whishlists.api';
import wishlistHearth from '@assets/whishlist-filled.svg';
import { HolidayWithWordpressHolidayData } from '@interfaces/holidays.interface';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Image,
  Stack,
  Text,
} from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';

import classes from './WishListCard.module.css';

interface WishListCardProps {
  data: HolidayWithWordpressHolidayData;
}

export default function WishListCard({ data }: WishListCardProps) {
  // ==========================================================================
  // General
  // ==========================================================================
  const { t } = useTranslation();

  // ==========================================================================
  // Api
  // ==========================================================================
  const [removeFromWhishlist] = useRemoveWhishlistedHolidayMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleRemoveFromWhishlistClick = (holidayId: string) => {
    openConfirmModal({
      title: t('wishlist.card.remove.title'),
      children: t('wishlist.card.remove.message'),
      confirmProps: { color: 'red' },
      labels: {
        confirm: t('wishlist.card.remove.confirm'),
        cancel: t('wishlist.card.remove.cancel'),
      },
      onConfirm: async () => {
        try {
          await removeFromWhishlist(holidayId).unwrap();
        } catch (e) {
          console.error(e);
        }
      },
    });
  };
  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Card withBorder radius="lg" className={classes.card}>
      <Card.Section className={classes.cardSection}>
        <Image
          src={data.wordpressData.image}
          style={{ height: '250px' }}
          fallbackSrc="https://placehold.co/300x250?text=Nessuna immagine"
        />
      </Card.Section>

      <ActionIcon
        className={classes.rating}
        size={'2.7rem'}
        onClick={() => handleRemoveFromWhishlistClick(data.id)}
      >
        <Image src={wishlistHearth} />
      </ActionIcon>
      <Stack flex={1} gap={0}>
        <Group mb="md" mt="xs" justify="space-between" align="center">
          <Box style={{ flex: 1, textAlign: 'center' }}>
            <Text fw="600" tt="uppercase">
              {data.wordpressData.durata_vacanza}
            </Text>
          </Box>
          <Divider orientation="vertical" color="#3E3E3E" size="sm" />
          <Box style={{ flex: 1, textAlign: 'center' }}>
            <Text fz="0.9rem" fw="600">
              {t('wishlist.card.perPerson')}
            </Text>
            <Text c="#e51c9d" fz="1.2rem" fw="800" lh="1.2rem" ta="center">
              €{data.wordpressData.quota_individuale}
            </Text>
          </Box>
        </Group>
        <Box mb="md">
          <Text fw={600} c="#626262" fz="sm">
            {data.wordpressData.codice_vacanza}
          </Text>
          <Text fw={600} fz="lg" c="#002D72" lh="1.2rem">
            {data.wordpressData.title}
          </Text>
        </Box>
      </Stack>

      <Group justify="end">
        <Button
          bg="#e51c9d"
          component="a"
          href={data.wordpressData.linkWpHoliday}
          target="_blank"
          size="sm"
        >
          {t('wishlist.card.discover')}
        </Button>
      </Group>
    </Card>
  );
}
